import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private isBrowser: boolean;
  /** storage keys */
  private readonly CONTENT_ONLY_CONTEXT_KEY = 'layout.contentOnly';
  private readonly NAVIGATION_OPEN_KEY = 'layout.navigationOpen';
  private readonly NAVIGATION_OPEN_DESKTOP_KEY = 'layout.navigationOpen.desktop';
  private readonly NAVIGATION_NARROW_KEY = 'layout.navigationNarrow';
  private readonly LEFT_OPEN_KEY   = 'layout.leftOpen';
  private readonly RIGHT_OPEN_KEY  = 'layout.rightOpen';
  private readonly LEFT_NARROW_KEY = 'layout.leftNarrow';
  private readonly SUPPLEMENTARY_TABS_NARROW_DESKTOP_KEY = 'layout.supplementaryTabsNarrow.desktop';
  private readonly SUPPLEMENTARY_TABS_NARROW_MOBILE_KEY = 'layout.supplementaryTabsNarrow.mobile';
  private readonly SUPPLEMENTARY_TABS_OPEN_DESKTOP_KEY = 'layout.supplementaryTabsOpen.desktop';
  private readonly SUPPLEMENTARY_TABS_OPEN_MOBILE_KEY = 'layout.supplementaryTabsOpen.mobile';
  private readonly INIT_KEY        = 'layout.initialized';
  private readonly LEFT_WIDTH_KEY  = 'layout.leftWidth';
  private readonly RIGHT_WIDTH_KEY = 'layout.rightWidth';

  /** backing fields */
  private _navigationOpen!: boolean;
  private _navigationNarrow!: boolean;
  public _leftOpen!: boolean;
  private _rightOpen!: boolean;
  private _leftNarrow!: boolean;
  private _supplementaryTabsNarrow!: boolean;
  private _supplementaryTabsOpen!: boolean;
  private _initialized!: boolean;
  private _leftWidth!: number;
  private _rightWidth!: number;
  private _contentOnlyMode!: boolean;
  private _previewShellMode = false;
  private _previewShellEntryUrl: string | null = null;
  private _toolbarVisible = true; // Session-only state, always starts visible
  private _nextRouteButtonsVisible = true; // Session-only state for next/previous buttons
  private _justNavigatedHome = false; // Tracks if logo was just clicked to go home
  private _navigatedHomeTimeout: any;
  
  // Zen mode: stores pre-zen state so we can restore when exiting zen mode
  private _zenModeActive = false;
  private _preZenState: {
    navigationOpen: boolean;
    supplementaryTabsOpen: boolean;
    toolbarVisible: boolean;
    nextRouteButtonsVisible: boolean;
  } | null = null;

  /** Loading state */
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._isLoading$.asObservable();

  /** Subject for keyboard shortcuts events */
  public helpPopoverToggle$ = new Subject<void>();
  public toggleNavigationMenu$ = new Subject<void>();
  public toggleNavigationWidth$ = new Subject<void>();
  public toggleLeftPanel$ = new Subject<void>();
  public toggleRightPanel$ = new Subject<void>();
  public toggleRightDetailPanel$ = new Subject<void>();
  public toggleLeftNarrow$ = new Subject<void>();
  public toggleSupplementaryTabsWidth$ = new Subject<void>();
  public toggleSupplementaryTabs$ = new Subject<void>();
  public toggleAgencySwitcher$ = new Subject<void>();
  public toggleProfilePopover$ = new Subject<void>();
  public toggleNotificationsPopover$ = new Subject<void>();
  public cycleToNextTab$ = new Subject<void>();
  public cycleToPreviousTab$ = new Subject<void>();
  public cycleToNextItem$ = new Subject<void>();
  public cycleToPreviousItem$ = new Subject<void>();
  public pressLastCta$ = new Subject<void>();
  public pressSecondToLastCta$ = new Subject<void>();
  public pressThirdToLastCta$ = new Subject<void>();
  public goHome$ = new Subject<void>();
  public toggleToolbar$ = new Subject<void>();
  public navigateToNextSegment$ = new Subject<void>();
  public navigateToPreviousSegment$ = new Subject<void>();
  public triggerFirstUtility$ = new Subject<void>();
  public triggerSecondUtility$ = new Subject<void>();
  public triggerThirdUtility$ = new Subject<void>();
  public focusChatInput$ = new Subject<void>();
  public navigateToParentTitle$ = new Subject<void>();
  public cycleActionsPaletteDisplayMode$ = new Subject<void>();
  public openScreenRecorder$ = new Subject<void>();
  public toggleZenMode$ = new Subject<void>();

  /** getters / setters that transparently persist */
  get navigationOpen(): boolean { return this._navigationOpen; }
  set navigationOpen(v: boolean) {
    this._navigationOpen = v;

    if (!this.isSmallDevice()) {
      this.saveFlag(this.NAVIGATION_OPEN_DESKTOP_KEY, v);
    }
  }

  get navigationNarrow(): boolean { return this._navigationNarrow; }
  set navigationNarrow(v: boolean) { this._navigationNarrow = v; this.saveFlag(this.NAVIGATION_NARROW_KEY, v); }

  get leftOpen(): boolean       { return this._leftOpen; }
  set leftOpen(v: boolean)      { this._leftOpen = v;  this.saveFlag(this.LEFT_OPEN_KEY, v); }

  get rightOpen(): boolean      { return this._rightOpen; }
  set rightOpen(v: boolean)     { this._rightOpen = v; this.saveFlag(this.RIGHT_OPEN_KEY, v); }

  get leftNarrow(): boolean     { return this._leftNarrow; }
  set leftNarrow(v: boolean)    { this._leftNarrow = v; this.saveFlag(this.LEFT_NARROW_KEY, v); }

  get supplementaryTabsNarrow(): boolean { return this._supplementaryTabsNarrow; }
  set supplementaryTabsNarrow(v: boolean) { this._supplementaryTabsNarrow = v; this.saveFlag(this.getSupplementaryTabsNarrowKey(), v); }

  get supplementaryTabsOpen(): boolean { return this._supplementaryTabsOpen; }
  set supplementaryTabsOpen(v: boolean) { this._supplementaryTabsOpen = v; this.saveFlag(this.getSupplementaryTabsOpenKey(), v); }

  get initialized(): boolean    { return this._initialized; }
  set initialized(v: boolean)   { this._initialized = v; this.saveFlag(this.INIT_KEY, v); }

  get leftWidth(): number       { return this._leftWidth; }
  set leftWidth(v: number)      { this._leftWidth = v; this.saveNumber(this.LEFT_WIDTH_KEY, v); }

  get rightWidth(): number      { return this._rightWidth; }
  set rightWidth(v: number)     { this._rightWidth = v; this.saveNumber(this.RIGHT_WIDTH_KEY, v); }

  get contentOnlyMode(): boolean { return this._contentOnlyMode; }
  set contentOnlyMode(v: boolean) { this._contentOnlyMode = v; this.saveBrowsingContextFlag(this.CONTENT_ONLY_CONTEXT_KEY, v); }

  get previewShellMode(): boolean { return this._previewShellMode; }
  set previewShellMode(v: boolean) { this._previewShellMode = v; }

  get previewShellEntryUrl(): string | null { return this._previewShellEntryUrl; }
  set previewShellEntryUrl(v: string | null) { this._previewShellEntryUrl = v; }

  get toolbarVisible(): boolean { return this._toolbarVisible; }
  set toolbarVisible(v: boolean) { this._toolbarVisible = v; } // No localStorage persistence

  get nextRouteButtonsVisible(): boolean { return this._nextRouteButtonsVisible; }
  set nextRouteButtonsVisible(v: boolean) { this._nextRouteButtonsVisible = v; } // No localStorage persistence

  get zenModeActive(): boolean { return this._zenModeActive; }

  /** Check if logo was just clicked to navigate home (for debounce logic) */
  get justNavigatedHome(): boolean { return this._justNavigatedHome; }

  /** Mark that the logo was just clicked to navigate home - applies 1 second debounce */
  markNavigatedHome(): void {
    this._justNavigatedHome = true;
    
    // Clear any existing timeout
    if (this._navigatedHomeTimeout) {
      clearTimeout(this._navigatedHomeTimeout);
    }
    
    // Reset flag after 1 second
    this._navigatedHomeTimeout = setTimeout(() => {
      this._justNavigatedHome = false;
    }, 1000);
  }

  // helpers -------------------------------------------------------------
  private loadFlag(key: string, defaultValue: boolean = false): boolean {
    if (!this.isBrowser) return defaultValue;
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : defaultValue;
  }

  private hasStoredFlag(key: string): boolean {
    return this.isBrowser && localStorage.getItem(key) !== null;
  }

  private saveFlag(key: string, val: boolean): void {
    if (!this.isBrowser) return;
    localStorage.setItem(key, JSON.stringify(val));
  }

  private loadNumber(key: string, defaultValue: number): number {
    if (!this.isBrowser) return defaultValue;
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : defaultValue;
  }

  private saveNumber(key: string, val: number): void {
    if (!this.isBrowser) return;
    localStorage.setItem(key, JSON.stringify(val));
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private loadBrowsingContextFlag(key: string, defaultValue: boolean = false): boolean {
    if (!this.isBrowser || typeof window === 'undefined') return defaultValue;

    try {
      const pattern = new RegExp(`(?:^|\\|)${this.escapeRegex(key)}=(1|0)(?:\\||$)`);
      const match = window.name.match(pattern);
      return match ? match[1] === '1' : defaultValue;
    } catch (_error) {
      return defaultValue;
    }
  }

  private saveBrowsingContextFlag(key: string, val: boolean): void {
    if (!this.isBrowser || typeof window === 'undefined') return;

    try {
      const token = `${key}=${val ? '1' : '0'}`;
      const pattern = new RegExp(`(?:^|\\|)${this.escapeRegex(key)}=(?:1|0)(?=\\||$)`);
      const currentName = typeof window.name === 'string' ? window.name : '';

      if (pattern.test(currentName)) {
        window.name = currentName.replace(pattern, (match) => match.startsWith('|') ? `|${token}` : token);
        return;
      }

      window.name = currentName ? `${currentName}|${token}` : token;
    } catch (_error) {
      // Ignore storage failures for session-only UI preferences.
    }
  }

  /** Device detection and default state helpers */
  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private location: Location
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    
    // Initialize backing fields after isBrowser is set
    this._navigationOpen = this.getInitialNavigationOpen();
    this._navigationNarrow = this.loadFlag(this.NAVIGATION_NARROW_KEY, true);
    this._leftOpen = this.loadFlag(this.LEFT_OPEN_KEY, this.getDefaultLeftOpen());
    this._rightOpen = this.loadFlag(this.RIGHT_OPEN_KEY, this.getDefaultRightOpen());
    this._leftNarrow = this.loadFlag(this.LEFT_NARROW_KEY, false);
    this._supplementaryTabsNarrow = this.loadFlag(
      this.getSupplementaryTabsNarrowKey(),
      this.getDefaultSupplementaryTabsNarrow()
    );
    this._supplementaryTabsOpen = this.loadFlag(
      this.getSupplementaryTabsOpenKey(),
      this.getDefaultSupplementaryTabsOpen()
    );
    this._initialized = this.loadFlag(this.INIT_KEY, false);
    this._leftWidth = this.loadNumber(this.LEFT_WIDTH_KEY, 260);
    this._rightWidth = this.loadNumber(this.RIGHT_WIDTH_KEY, 260);
    this._contentOnlyMode = this.loadBrowsingContextFlag(this.CONTENT_ONLY_CONTEXT_KEY, false);
  }

  private isSmallDevice(): boolean {
    // Check if window is available (for SSR compatibility)
    if (typeof window === 'undefined') {
      return false;
    }
    
    // Match the layout component's mobile breakpoint.
    return window.innerWidth < 768;
  }

  private getDefaultLeftOpen(): boolean {
    return false;
    return !this.isSmallDevice(); // Open on larger devices, closed on small devices
  }

  private getDefaultNavigationOpen(): boolean {
    return !this.isSmallDevice(); // Open on larger devices, closed on small devices
  }

  private getInitialNavigationOpen(): boolean {
    if (this.isSmallDevice()) {
      return false;
    }

    if (this.hasStoredFlag(this.NAVIGATION_OPEN_DESKTOP_KEY)) {
      return this.loadFlag(this.NAVIGATION_OPEN_DESKTOP_KEY, this.getDefaultNavigationOpen());
    }

    return this.loadFlag(this.NAVIGATION_OPEN_KEY, this.getDefaultNavigationOpen());
  }

  private getDefaultRightOpen(): boolean {
    return false; // Always open by default
  }

  private getDefaultSupplementaryTabsNarrow(): boolean {
    return this.isSmallDevice();
  }

  private getDefaultSupplementaryTabsOpen(): boolean {
    return !this.isSmallDevice();
  }

  private getSupplementaryTabsNarrowKey(): string {
    return this.isSmallDevice()
      ? this.SUPPLEMENTARY_TABS_NARROW_MOBILE_KEY
      : this.SUPPLEMENTARY_TABS_NARROW_DESKTOP_KEY;
  }

  private getSupplementaryTabsOpenKey(): string {
    return this.isSmallDevice()
      ? this.SUPPLEMENTARY_TABS_OPEN_MOBILE_KEY
      : this.SUPPLEMENTARY_TABS_OPEN_DESKTOP_KEY;
  }

  public applySupplementaryTabsViewportDefaults(): void {
    const openKey = this.getSupplementaryTabsOpenKey();
    const narrowKey = this.getSupplementaryTabsNarrowKey();

    if (this.hasStoredFlag(openKey)) {
      this._supplementaryTabsOpen = this.loadFlag(openKey, this.getDefaultSupplementaryTabsOpen());
    } else {
      this._supplementaryTabsOpen = this.getDefaultSupplementaryTabsOpen();
    }

    if (this.hasStoredFlag(narrowKey)) {
      this._supplementaryTabsNarrow = this.loadFlag(narrowKey, this.getDefaultSupplementaryTabsNarrow());
      return;
    }

    this._supplementaryTabsNarrow = this.getDefaultSupplementaryTabsNarrow();
  }

  public applyNavigationViewportDefaults(): void {
    if (this.isSmallDevice()) {
      this._navigationOpen = false;
      return;
    }

    if (this.hasStoredFlag(this.NAVIGATION_OPEN_DESKTOP_KEY)) {
      this._navigationOpen = this.loadFlag(this.NAVIGATION_OPEN_DESKTOP_KEY, this.getDefaultNavigationOpen());
      return;
    }

    this._navigationOpen = this.loadFlag(this.NAVIGATION_OPEN_KEY, this.getDefaultNavigationOpen());
  }

  /** Methods for keyboard shortcuts */
  public toggleHelpPopover(): void {
    this.helpPopoverToggle$.next();
  }

  public toggleNavigationMenu(): void {
    this.toggleNavigationMenu$.next();
  }

  public toggleNavigationWidth(): void {
    this.toggleNavigationWidth$.next();
  }

  public activatePreviewShell(entryUrl: string): void {
    this._previewShellMode = true;
    this._previewShellEntryUrl = entryUrl;
  }

  public clearPreviewShell(): void {
    this._previewShellMode = false;
    this._previewShellEntryUrl = null;
  }

  public toggleLeftPanel(): void {
    this.toggleLeftPanel$.next();
  }

  public toggleRightPanel(): void {
    this.toggleRightPanel$.next();
  }

  public toggleRightDetailPanel(): void {
    this.toggleRightDetailPanel$.next();
  }

  public toggleSupplementaryTabsWidth(): void {
    this.toggleSupplementaryTabsWidth$.next();
  }

  public toggleSupplementaryTabs(): void {
    this.toggleSupplementaryTabs$.next();
  }

  public toggleLeftNarrow(): void {
    this.toggleLeftNarrow$.next();
  }

  public toggleAgencySwitcher(): void {
    this.toggleAgencySwitcher$.next();
  }

  public toggleProfilePopover(): void {
    this.toggleProfilePopover$.next();
  }

  public toggleNotificationsPopover(): void {
    this.toggleNotificationsPopover$.next();
  }

  public goBack(): void {
    this.location.back();
  }

  public goForward(): void {
    this.location.forward();
  }

  public cycleToNextTab(): void {
    this.cycleToNextTab$.next();
  }

  public cycleToPreviousTab(): void {
    this.cycleToPreviousTab$.next();
  }

  public cycleToNextItem(): void {
    this.cycleToNextItem$.next();
  }

  public cycleToPreviousItem(): void {
    this.cycleToPreviousItem$.next();
  }

  public pressLastCta(): void {
    this.pressLastCta$.next();
  }

  public pressSecondToLastCta(): void {
    this.pressSecondToLastCta$.next();
  }

  public pressThirdToLastCta(): void {
    this.pressThirdToLastCta$.next();
  }

  public goHome(): void {
    this.goHome$.next();
  }

  public toggleToolbar(): void {
    this.toggleToolbar$.next();
  }

  public toggleNextRouteButtons(): void {
    this._nextRouteButtonsVisible = !this._nextRouteButtonsVisible;
  }

  /** Toggle zen mode - hides/shows all UI chrome (nav menu, tabs, toolbar, next/prev buttons) */
  public toggleZenMode(): void {
    if (!this._zenModeActive) {
      // Entering zen mode - save current state and hide everything
      this._preZenState = {
        navigationOpen: this._navigationOpen,
        supplementaryTabsOpen: this._supplementaryTabsOpen,
        toolbarVisible: this._toolbarVisible,
        nextRouteButtonsVisible: this._nextRouteButtonsVisible
      };
      
      this.navigationOpen = false;
      this.supplementaryTabsOpen = false;
      this._toolbarVisible = false;
      this._nextRouteButtonsVisible = false;
      this._zenModeActive = true;
    } else {
      // Exiting zen mode - restore previous state
      if (this._preZenState) {
        this.navigationOpen = this._preZenState.navigationOpen;
        this.supplementaryTabsOpen = this._preZenState.supplementaryTabsOpen;
        this._toolbarVisible = this._preZenState.toolbarVisible;
        this._nextRouteButtonsVisible = this._preZenState.nextRouteButtonsVisible;
      } else {
        // Fallback: show everything
        this.navigationOpen = true;
        this.supplementaryTabsOpen = true;
        this._toolbarVisible = true;
        this._nextRouteButtonsVisible = true;
      }
      this._preZenState = null;
      this._zenModeActive = false;
    }
    
    this.toggleZenMode$.next();
  }

  /** Segment navigation methods */
  public cycleToNextSegment(): void {
    this.navigateToNextSegment$.next();
  }

  public cycleToPreviousSegment(): void {
    this.navigateToPreviousSegment$.next();
  }

  public navigateToParentTitle(): void {
    this.navigateToParentTitle$.next();
  }

  /** Utility keyboard shortcuts methods */
  public triggerFirstUtility(): void {
    this.triggerFirstUtility$.next();
  }

  public triggerSecondUtility(): void {
    this.triggerSecondUtility$.next();
  }

  public triggerThirdUtility(): void {
    this.triggerThirdUtility$.next();
  }

  public focusChatInput(): void {
    this.focusChatInput$.next();
  }

  /** Actions palette methods */
  public cycleActionsPaletteDisplayMode(): void {
    this.cycleActionsPaletteDisplayMode$.next();
  }

  /** Screen recorder methods */
  public openScreenRecorder(): void {
    this.openScreenRecorder$.next();
  }

  /** Loading state methods */
  public showLoader(): void {
    this._isLoading$.next(true);
  }

  public hideLoader(): void {
    this._isLoading$.next(false);
  }

  public setLoading(loading: boolean): void {
    this._isLoading$.next(loading);
  }
}
